// eslint-disable-next-line
import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import { Panel, Box, theme, Button, Flex, Text } from '@xometry/xometry_loft';
import {
  PaymentMethodsQuery,
  GetLinkTokenQuery,
} from '../../schema/generated/schema';
import PaymentMethodsTable from './PaymentMethodsTable';
import PaymentMethodModal from './PaymentMethodModal/index.jsx';
import AddCardModal from './AddCardModal';

export const mapMethodsDataToPayments = (paymentMethods: any) => {
  return paymentMethods
    .filter((paymentMethod: any) => paymentMethod.type !== 'availablebalance')
    .map((paymentMethod: any) => ({
      id: paymentMethod.id,
      name:
        paymentMethod.name ||
        paymentMethod.accountHolderName ||
        paymentMethod.businessName ||
        null,
      last4: paymentMethod.last4 || null,
      expMonth: paymentMethod.expMonth || null,
      expYear: paymentMethod.expYear || null,
      type: paymentMethod.type,
      status: paymentMethod.status || null,
      brand: paymentMethod.brand || null,
      bankName: paymentMethod.bankName || null,
    }));
};

interface Props {
  userID: any;
}

export interface CardPayment {
  bankName: null;
  brand: string;
  expMonth: string;
  expYear: string;
  id: string;
  last4: string;
  name: string;
  status: string | null;
  type: string;
}

export interface BankAccountPayment {
  bankName: string;
  brand: null;
  expMonth: null;
  expYear: null;
  id: string;
  last4: string;
  name: null;
  status: string | null;
  type: string;
}

export interface MSTSPayment {
  bankName: null;
  brand: null;
  expMonth: null;
  expYear: null;
  id: string;
  last4: null;
  name: null;
  status: string | null;
  type: string;
}

const PaymentMethods = ({ userID }: Props) => {
  const [payments, setPayments] = useState<
    (CardPayment | BankAccountPayment | MSTSPayment)[] | undefined
  >();
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);

  const {
    loading: methodsLoading,
    data: methodsData,
    refetch: methodsRefetch,
  } = useQuery(PaymentMethodsQuery);

  const {
    loading: linkTokenLoading,
    data: linkTokenData,
    refetch: linkTokenRefetch,
  } = useQuery(GetLinkTokenQuery);

  useEffect(() => {
    if (!methodsLoading) {
      if (methodsData?.paymentMethods) {
        const mappedMethodsData = mapMethodsDataToPayments(
          methodsData.paymentMethods
        );
        setPayments(mappedMethodsData);
      }
    }
  }, [methodsData, methodsLoading]);

  const handleMstsPolling = (refetch: any) => {
    const pollingMsts = setInterval(() => {
      refetch();
    }, 5000); // 5 secs
    if (methodsData) {
      const mstsFilter = methodsData?.paymentMethods.filter(
        (method: any) => method?.type === 'msts'
      );
      if (mstsFilter[0]?.status === 'Approved') {
        clearInterval(pollingMsts);
      }
    }
  };

  if (methodsLoading || linkTokenLoading) return <div />;

  return (
    <Box>
      <Text kind="subpage-title" display="block" marginBottom={theme.space[4]}>
        Payment Methods
      </Text>
      <Flex flexDirection={['column', 'column', 'row']}>
        <Panel
          kind="primary"
          maxWidth="792px"
          width="100%"
          css={css`
            box-sizing: border-box;
            height: -moz-max-content;
          `}
        >
          {payments && (
            <PaymentMethodsTable
              paymentsData={payments}
              methodsRefetch={methodsRefetch}
            />
          )}
          {linkTokenData && (
            <>
              <Box width={['auto', '218px']}>
                <Button
                  scale="small"
                  kind="text-blue"
                  padding={`${theme.space[1]} ${theme.space[2]}`}
                  onClick={() => setPaymentMethodModalOpen(true)}
                  css={css`
                    &:hover {
                      background: ${theme.colors.blues[10]};
                    }
                  `}
                >
                  <Box fontSize={theme.fontSizes[0]}>Add a Payment Method</Box>
                </Button>
              </Box>
              <PaymentMethodModal
                paymentMethodModalOpen={paymentMethodModalOpen}
                setPaymentMethodModalOpen={setPaymentMethodModalOpen}
                methodsRefetch={methodsRefetch}
                linkTokenData={linkTokenData}
                linkTokenLoading={linkTokenLoading}
                linkTokenRefetch={linkTokenRefetch}
                userID={userID}
                handleMstsPolling={handleMstsPolling}
                openStripeCardModal={() => setAddCardModalOpen(true)}
              />
            </>
          )}
        </Panel>
      </Flex>
      <AddCardModal
        setAddCardModalOpen={setAddCardModalOpen}
        addCardModalOpen={addCardModalOpen}
        methodsRefetch={methodsRefetch}
      />
    </Box>
  );
};

export default PaymentMethods;
